// Render-time guard for the legacy "from EUR ∞" price artifact.
//
// 63 of the 139 auto-generated seoContent docs (43 route, 20 dest as of
// 2026-07-07) carry a literal "EUR ∞" baked into their PROSE STRINGS
// (metaDesc, faqs[].a, keyFacts[].value) — a Math.min-over-empty artifact of
// the removed ContentLab generator. The CMS is live production data and is
// NOT rewritten; instead every surface that renders seoContent text passes it
// through this sanitizer first:
//   - substitute the REAL current minimum ("EUR 5.170", de-DE — the same
//     route.cardFromPrice source every price surface uses) when one exists,
//   - otherwise DROP the sentence containing the broken token entirely,
//     never publishing "∞" / "EUR Infinity" in meta, JSON-LD or on-page copy.
// Display-only and read-only: no price is computed here, values come from the
// precomputed route.cardFromPrice mins (fleet-card-price.js helpers).
//
// NOTE: a bare word "Infinity" is NOT treated as broken ("Infinity pool" is a
// legitimate hotel feature) — only the ∞ glyph and the "EUR Infinity" form.
import { routeFromPriceMin, routesFromPriceMin } from './fleet-card-price.js';

const BROKEN_PRICE_RE = /∞|EUR\s*Infinity\b/; // ∞ anywhere, or "EUR Infinity"

// Sanitize one text string. Returns the input unchanged when clean, the text
// with "EUR ∞" replaced by the real "EUR X" (de-DE) when a positive finite
// min is supplied, the text minus the broken sentence(s) otherwise, or null
// when nothing survives (callers fall back to their own default copy).
export function sanitizePriceText(text, min) {
  if (typeof text !== 'string' || !text) return text;
  if (!BROKEN_PRICE_RE.test(text)) return text;
  const n = Number(min);
  if (Number.isFinite(n) && n > 0) {
    const amount = n.toLocaleString('de-DE');
    return text
      .replace(/EUR\s*(?:∞|Infinity\b)/g, 'EUR ' + amount)
      .replace(/∞/g, amount);
  }
  // No real price available — drop each sentence carrying the broken token.
  const kept = text
    .split(/(?<=[.!?])\s+/)
    .filter(part => !BROKEN_PRICE_RE.test(part));
  const out = kept.join(' ').trim();
  return out || null;
}

// Sanitize a whole seoContent doc (h1/metaTitle/metaDesc/intro/longDesc/...,
// faqs[].q/a, keyFacts[].label/value, highlights[].*). Walks every string in
// the doc; strings that sanitize to null are dropped from their parent, and a
// FAQ item that loses its question or answer is removed entirely (an answer
// of nothing is worse than no FAQ row). Non-doc inputs pass through.
export function sanitizeSeoContentDoc(doc, min) {
  if (!doc || typeof doc !== 'object') return doc;
  const walk = (v) => {
    if (typeof v === 'string') return sanitizePriceText(v, min);
    if (Array.isArray(v)) return v.map(walk).filter(x => x !== null && x !== undefined);
    if (v && typeof v === 'object') {
      const out = {};
      for (const [k, val] of Object.entries(v)) {
        const w = walk(val);
        if (w !== null && w !== undefined) out[k] = w;
      }
      return out;
    }
    return v;
  };
  const out = walk(doc);
  if (Array.isArray(out.faqs)) {
    out.faqs = out.faqs.filter(f => f && (f.q || f.question) && (f.a || f.answer));
  }
  return out;
}

// The entity-appropriate finite minimum for price substitution — the SAME
// existing sources the visible price surfaces already use (read-only):
//   route       → min over its precomputed cardFromPrice map
//   destination → min over every Athens→destination route's cardFromPrice
//   hotel       → min over every Athens→hotel-city route's cardFromPrice
//   anything else → null (sentence-drop fallback; no price is invented)
export function entitySeoPriceMin(entityType, entity, routes) {
  if (!entity) return null;
  if (entityType === 'route') return routeFromPriceMin(entity.cardFromPrice);
  if (entityType === 'destination' || entityType === 'dest') {
    return routesFromPriceMin((routes || []).filter(r => r && r.from === 'Athens' && r.to === entity.name));
  }
  if (entityType === 'hotel') {
    if (!entity.city) return null;
    return routesFromPriceMin((routes || []).filter(r => r && r.from === 'Athens' && r.to === entity.city));
  }
  return null;
}
